/**
 * NotebookReconcilePanel — the "Notebook" recording-style panel for a Phase-1
 * category step in the Full Audit Wizard. The owner writes transactions in a
 * notebook first; here the auditor walks each ENCODED record in sequence and
 * marks it "Matches notebook" (identical) or flags a mismatch (with a note).
 * Anything in the notebook but NOT yet in AgriBooks is added via Quick
 * Late-Encode (a real back-dated transaction on the existing encode page).
 *
 * Reuses GET /api/audit-wizard/reconcile-list and persists marks via
 * POST /api/audit-wizard/sessions/{id}/reconcile-mark.
 */
import { useState, useEffect, useCallback } from 'react';
import { api } from '../../../contexts/AuthContext';
import { formatPHP } from '../../../lib/utils';
import { Input } from '../../ui/input';
import { Button } from '../../ui/button';
import {
  Search, Check, Flag, Loader2, NotebookPen, ExternalLink, FileSearch,
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Where "Add from notebook" (quick late-encode) opens for each category.
const ENCODE_ROUTE = {
  credits: '/sales-new',
  cash_outs: '/expenses',
  digital: '/sales-new',
  expenses: '/expenses',
  purchase_orders: '/purchase-orders',
  transfers: '/branch-transfers',
};

export default function NotebookReconcilePanel({ session, stepCategory }) {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({ total: 0, identical: 0, flagged: 0, pending: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [noteFor, setNoteFor] = useState(null); // doc_id whose note input is open
  const [noteText, setNoteText] = useState('');
  const [savingId, setSavingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ category: stepCategory, session_id: session.id });
      if (session?.branch_id) params.set('branch_id', session.branch_id);
      if (session?.period_from) params.set('period_from', session.period_from);
      if (session?.period_to) params.set('period_to', session.period_to);
      if (search.trim()) params.set('search', search.trim());
      const res = await api.get(`${BACKEND_URL}/api/audit-wizard/reconcile-list?${params}`);
      setItems(res.data.items || []);
      setSummary(res.data.summary || {});
    } catch { setItems([]); }
    setLoading(false);
  }, [stepCategory, session.id, session.branch_id, session.period_from, session.period_to, search]);

  useEffect(() => {
    const t = setTimeout(load, search ? 300 : 0);
    return () => clearTimeout(t);
  }, [load, search]);

  const mark = async (it, status, note = '') => {
    // toggling the active status off → back to pending
    const next = it.reconcile_status === status && status !== 'flagged' ? 'pending' : status;
    setSavingId(it.id);
    try {
      await api.post(`${BACKEND_URL}/api/audit-wizard/sessions/${session.id}/reconcile-mark`, {
        category: stepCategory, doc_id: it.id, status: next, note,
      });
      setItems(prev => prev.map(r => r.id === it.id ? { ...r, reconcile_status: next, reconcile_note: note } : r));
      setSummary(prev => {
        const m = { ...prev };
        // recompute from items copy
        const updated = items.map(r => r.id === it.id ? { ...r, reconcile_status: next } : r);
        m.identical = updated.filter(r => r.reconcile_status === 'identical').length;
        m.flagged = updated.filter(r => r.reconcile_status === 'flagged').length;
        m.pending = updated.filter(r => r.reconcile_status === 'pending').length;
        return m;
      });
      if (next === 'flagged') { setNoteFor(null); setNoteText(''); }
    } catch { toast.error('Could not save mark'); }
    setSavingId(null);
  };

  const openFlag = (it) => {
    setNoteFor(it.id);
    setNoteText(it.reconcile_note || '');
  };

  const reviewed = summary.identical + summary.flagged;
  const pct = summary.total > 0 ? Math.round((reviewed / summary.total) * 100) : 0;

  return (
    <div className="space-y-3" data-testid="notebook-reconcile-panel">
      {/* Progress */}
      <div className="rounded-lg border border-slate-200 p-3">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-xs font-semibold text-slate-600">{reviewed} reconciled / {summary.total}</span>
          <div className="flex items-center gap-3 text-[11px]">
            <span className="text-emerald-600 flex items-center gap-1"><Check size={12} />{summary.identical} matched</span>
            {summary.flagged > 0 && <span className="text-red-600 flex items-center gap-1"><Flag size={12} />{summary.flagged} flagged</span>}
          </div>
        </div>
        <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
          <div className="h-full bg-[#1A4D2E] transition-all" style={{ width: `${pct}%` }} data-testid="reconcile-progress-bar" />
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by ref / party"
            className="pl-8 h-9 text-sm" data-testid="reconcile-search" />
        </div>
        <Button variant="outline" size="sm" className="h-9" data-testid="reconcile-add-from-notebook"
          onClick={() => {
            window.open(ENCODE_ROUTE[stepCategory] || '/sales-new', '_blank');
            toast.info('Encode the missing entry back-dated to its notebook date, then refresh this list.');
          }}>
          <NotebookPen size={14} className="mr-1.5" /> Add from notebook <ExternalLink size={12} className="ml-1.5 opacity-60" />
        </Button>
      </div>

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center py-12 text-slate-400"><Loader2 size={20} className="animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-slate-400">
          <FileSearch size={28} className="mb-2 opacity-40" />
          <p className="text-sm">No encoded records in this period.</p>
          <p className="text-xs mt-1">If your notebook has entries here, use “Add from notebook”.</p>
        </div>
      ) : (
        <div className="space-y-1.5 max-h-[460px] overflow-y-auto pr-1">
          {items.map(it => {
            const st = it.reconcile_status;
            return (
              <div key={it.id} data-testid={`reconcile-row-${it.id}`}
                className={`px-3 py-2.5 rounded-lg border transition-colors ${
                  st === 'identical' ? 'border-emerald-200 bg-emerald-50/40'
                    : st === 'flagged' ? 'border-red-200 bg-red-50/40' : 'border-slate-200'}`}>
                <div className="flex items-center gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-slate-800 truncate">
                      {it.label}
                      {it.ref && it.ref !== it.label && <span className="text-slate-400 font-normal"> · {it.ref}</span>}
                    </p>
                    <p className="text-xs text-slate-400 truncate">
                      {it.party && <span>{it.party} · </span>}{it.date} · {formatPHP(it.total)}
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button onClick={() => mark(it, 'identical')} disabled={savingId === it.id} data-testid={`reconcile-identical-${it.id}`}
                      title="Matches notebook"
                      className={`w-8 h-8 rounded-lg flex items-center justify-center border transition-colors ${
                        st === 'identical' ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-emerald-600 border-slate-200 hover:border-emerald-300'}`}>
                      <Check size={15} />
                    </button>
                    <button onClick={() => openFlag(it)} disabled={savingId === it.id} data-testid={`reconcile-flag-${it.id}`}
                      title="Flag a mismatch"
                      className={`w-8 h-8 rounded-lg flex items-center justify-center border transition-colors ${
                        st === 'flagged' ? 'bg-red-600 text-white border-red-600' : 'bg-white text-red-500 border-slate-200 hover:border-red-300'}`}>
                      <Flag size={14} />
                    </button>
                  </div>
                </div>

                {/* Flag note input */}
                {(noteFor === it.id || (st === 'flagged' && it.reconcile_note)) && (
                  <div className="flex items-center gap-2 mt-2">
                    <Input value={noteFor === it.id ? noteText : it.reconcile_note}
                      onChange={e => setNoteText(e.target.value)}
                      onFocus={() => { if (noteFor !== it.id) { setNoteFor(it.id); setNoteText(it.reconcile_note || ''); } }}
                      placeholder="What didn't match? (e.g. ₱50 less than notebook)"
                      className="h-8 text-xs" data-testid={`reconcile-note-${it.id}`} />
                    <Button size="sm" className="h-8 bg-red-600 hover:bg-red-700 text-white"
                      onClick={() => mark(it, 'flagged', noteText)} data-testid={`reconcile-flag-save-${it.id}`}>
                      Flag
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
